<script setup lang="ts">
import { computed, useSlots } from 'vue';
import Icon from '@components/Icon.vue';
import Typography from '@components/Typography.vue';
import { IconCaretDown } from '@icons';

interface DropdownProps {
  isOpen?: boolean,
}

const emit = defineEmits<{(e: 'toggleDropdown', payload: boolean): void }>();

const { isOpen = false } = defineProps<DropdownProps>();

const isDropdownOpen = computed<boolean>({
  get: () => isOpen,
  set: (value) => emit('toggleDropdown', value),
});

const toggleDropdown = (): void => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

const slots = useSlots();
const hasSlot = (slot: string): boolean => !!slots[slot];
</script>

<template>
  <div>
    <div
      class="dropdown-filter dropdown-filter-border"
      :class="[
        'h-16',
        'p-4',
        'gap-4',
        'flex items-center justify-between',
        'select-none',
        'cursor-pointer',
        'border',
        { 'hover:bg-gray-light': !isOpen },
      ]"
      @click="toggleDropdown"
    >
      <div class="relative flex min-w-0 flex-1 items-center gap-4">
        <div class="shrink-0">
          <slot name="icon" />
        </div>

        <div class="flex min-w-0 flex-1 flex-col md:max-w-[440px]">
          <template v-if="hasSlot('text')">
            <Typography
              variant="content-heading-2"
              class="block md:hidden"
            >
              <slot name="text" />
            </Typography>

            <Typography
              variant="body-bold"
              class="hidden md:block"
            >
              <slot name="text" />
            </Typography>
          </template>

          <Typography
            v-if="hasSlot('subtitle')"
            variant="body"
            color="gray-darker"
            class="block min-w-0 truncate"
          >
            <slot name="subtitle" />
          </Typography>
        </div>
      </div>

      <Icon
        :svg="IconCaretDown"
        width="24"
        height="24"
        class="ml-auto shrink-0"
        :class="{ 'rotate-180': isOpen }"
      />
    </div>

    <div
      v-if="isOpen"
      class="dropdown-filter-content px-6 pb-8 pt-1"
    >
      <slot name="panel" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@styles/mixins/rem' as *;
@use '@styles/variables/colors' as *;

.dropdown-filter-border {
  box-shadow: inset 0 toRem(1) 0 $colorGray;
}
</style>
